import { create, StoreApi } from 'zustand';
import { v4 as uuid } from 'uuid';
import { Notification, NotificationType } from '../types';
import { ChronosErrorBase } from '@/model/grpc-client/common-service-error';

export const NOTIFY_STANDARD_TIMEOUT = 5;

export interface NotificationsStore {
  notifications: Notification[];
  clearNotifications: () => void;
  notifyInfo: (message: string, timeoutInSeconds?: number) => void;
  notifySuccess: (message: string, timeoutInSeconds?: number) => void;
  notifyWarning: (message: string, timeoutInSeconds?: number) => void;
  notifyGameConnectionError: (message: string, error?: Error) => void;
  notifyRequestTimeout: (message: string, error?: Error) => void;
  notifyError: (message: string, error?: Error) => void;
  dismissNotification: (id: string) => void;
}

function extractDetails(error?: Error): string | undefined {
  if (error instanceof ChronosErrorBase && error.details) {
    return error.details;
  }
  return undefined;
}

function dismissAfterTimeout(
  get: StoreApi<NotificationsStore>['getState'],
  id: string,
  timeoutInSeconds?: number
) {
  if (timeoutInSeconds != null) {
    setTimeout(() => get().dismissNotification(id), timeoutInSeconds * 1000);
  }
}

const makeNotificationSetter =
  (
    set: StoreApi<NotificationsStore>['setState'],
    get: StoreApi<NotificationsStore>['getState'],
    type: NotificationType,
    defaultTimeOut?: number
  ) =>
  (message: string, timeoutInSeconds?: number) => {
    const id = uuid();
    set((state: NotificationsStore) => ({
      notifications: [...state.notifications, { id, type, message }],
    }));
    dismissAfterTimeout(get, id, timeoutInSeconds ?? defaultTimeOut);
  };

const makeErrorNotificationSetter =
  (set: StoreApi<NotificationsStore>['setState'], type: NotificationType) =>
  (message: string, error?: Error) => {
    if (error) {
      console.error('[Notification]', message, error);
    }
    set((state) => ({
      notifications: [
        ...state.notifications,
        { id: uuid(), type, message, details: extractDetails(error), error },
      ],
    }));
  };

export const useNotificationStore = create<NotificationsStore>((set, get) => ({
  notifications: [],
  clearNotifications: () => set({ notifications: [] }),
  notifyInfo: makeNotificationSetter(set, get, NotificationType.Info),
  notifySuccess: makeNotificationSetter(
    set,
    get,
    NotificationType.Success,
    NOTIFY_STANDARD_TIMEOUT
  ),
  notifyWarning: makeNotificationSetter(set, get, NotificationType.Warning),
  notifyGameConnectionError: makeErrorNotificationSetter(set, NotificationType.GameConnectionError),
  notifyRequestTimeout: makeErrorNotificationSetter(set, NotificationType.RequestTimeout),
  notifyError: makeErrorNotificationSetter(set, NotificationType.Error),
  dismissNotification: (id: string) =>
    set((state) => ({
      notifications: state.notifications.filter((notification) => notification.id !== id),
    })),
}));

export const getNotificationsStore = () => useNotificationStore;
